import { CSSProperties, PropsWithChildren, useState } from 'react'
import styled from '@emotion/styled'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { useMediaQuery } from 'usehooks-ts'

import { Flex } from './Flex'
import { Label } from './Typography'

import { TooltipArrow } from '@/assets/icons'
import { colors } from '@/constants/colors'

const ContentWrapper = styled(Flex)`
  padding: 1.25rem;
  color: ${colors.light2};
  background-color: ${colors.darkBlue2};
  box-shadow: 0px 30px 40px 0px #0000004d;
  border: 0.5px solid ${colors.darkBlue4};
  border-radius: 1.25rem;
`

interface TooltipProps
  extends PropsWithChildren,
    RadixTooltip.TooltipContentProps {
  tooltipContent: JSX.Element | string
  wrapperStyle?: CSSProperties
  textStyle?: CSSProperties
  arrowHidden?: boolean
  onlyOnHover?: boolean
}

export default function Tooltip({
  children,
  tooltipContent,
  align = 'end',
  side = 'bottom',
  sideOffset = 5,
  alignOffset = -60,
  textStyle = { fontWeight: 600 },
  wrapperStyle,
  arrowHidden = false,
  onlyOnHover = false,
  ...contentProps
}: TooltipProps) {
  const canHover = useMediaQuery('(hover: hover)')
  const [isOpen, setIsOpen] = useState(false)

  if (onlyOnHover && !canHover) return children

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        delayDuration={0}
        onOpenChange={setIsOpen}
        open={isOpen}
      >
        <RadixTooltip.Trigger asChild>
          <div
            onClick={(e) => {
              if (canHover) return
              e.preventDefault()
              setIsOpen(true)
            }}
            style={{ display: 'flex' }}
          >
            {children}
          </div>
        </RadixTooltip.Trigger>

        <RadixTooltip.Portal>
          <RadixTooltip.Content
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            side={side}
            align={align}
            {...contentProps}
          >
            <ContentWrapper style={wrapperStyle}>
              {typeof tooltipContent === 'string' ? (
                <Label style={textStyle}>{tooltipContent}</Label>
              ) : (
                tooltipContent
              )}
            </ContentWrapper>
            <RadixTooltip.Arrow asChild>
              <div
                style={{
                  margin: '-4.5px',
                  display: arrowHidden ? 'none' : 'block',
                }}
              >
                <TooltipArrow />
              </div>
            </RadixTooltip.Arrow>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}
